import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/* eslint-disable react-refresh/only-export-components -- shadcn co-locates the variant helper with its component */

const buttonVariants = cva(
  "cc-control group/button inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-sm border-[1.5px] font-sans text-sm font-semibold outline-none select-none transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-150 ease-out focus-visible:shadow-[var(--focus-ring)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none aria-invalid:border-destructive aria-invalid:shadow-[var(--field-focus-shadow)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "cc-lift border-primary-deep bg-primary text-primary-foreground shadow-button hover:bg-primary-hover hover:shadow-button-hover active:shadow-button-active",
        outline:
          "cc-lift border-input bg-card text-foreground shadow-button hover:border-primary hover:text-link hover:shadow-button-hover active:shadow-button-active aria-expanded:border-primary aria-expanded:text-link",
        secondary:
          "cc-lift border-input bg-secondary text-secondary-foreground shadow-button hover:border-primary hover:text-link hover:shadow-button-hover active:shadow-button-active",
        ghost:
          "border-transparent bg-transparent text-foreground shadow-none hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "cc-lift border-destructive-deep bg-destructive text-destructive-foreground shadow-button hover:shadow-button-hover active:shadow-button-active",
        link: "border-transparent text-link shadow-none underline-offset-4 hover:underline",
      },
      size: {
        default:
          "min-h-10 gap-2 px-[18px] py-[9px] has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        xs: "min-h-7 gap-1.5 px-2 py-1 text-xs [&_svg:not([class*='size-'])]:size-3",
        sm: "min-h-8 gap-1.5 px-3 py-[5px] text-[13px] [&_svg:not([class*='size-'])]:size-3.5",
        lg: "min-h-12 gap-2 px-[26px] py-[13px] text-[15px]",
        icon: "size-10 p-0",
        "icon-xs": "size-7 p-0 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 p-0",
        "icon-lg": "size-12 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
